import { alpha, decomposeColor, recomposeColor, type Theme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import { CONTENT_CHART_COLORS } from '../constants/contentColors';
import { CONTENT_FONT_FAMILY_VAR } from '../constants/fonts';
import { contentBorders } from './createContentTheme';

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/** Weakest shade of a single-hue ramp (share of the hue over the paper colour). */
const RAMP_MIN_STRENGTH = { light: 0.25, dark: 0.35 } as const;

/** `color` laid over `background` at `strength` (0–1), returned as a solid colour. */
export function mixOver(color: string, background: string, strength: number): string {
  const fg = decomposeColor(color).values;
  const bg = decomposeColor(background).values;
  const values = [0, 1, 2].map((i) => Math.round(bg[i] + (fg[i] - bg[i]) * strength)) as [number, number, number];
  return recomposeColor({ type: 'rgb', values });
}

/**
 * `count` shades of ZERO purple, strongest first, fading to 25% (35% in dark
 * mode) — one hue for compositions where segments are parts of the same whole
 * (materials, statuses of one population). Solid colours (mixed into the
 * paper colour) so bars and separators stay crisp.
 */
export function accentRamp(theme: Theme, count: number): string[] {
  const { main } = theme.palette.primary;
  const paper = theme.palette.background.paper;
  if (count <= 1) return [main];
  return Array.from({ length: count }, (_, i) =>
    mixOver(main, paper, 1 - (i * (1 - RAMP_MIN_STRENGTH[theme.palette.mode])) / (count - 1)),
  );
}

/**
 * Margin for charts with a day axis: the last date label is centred on the
 * last day and needs room past the plot ("31 ديسمبر" is ~60 px wide).
 */
export const DAY_AXIS_CHART_MARGIN = { top: 8, right: 32, bottom: 0, left: 0 } as const;

/**
 * Y-axis width (px) that fits its longest tick label — compact labels run
 * longer in some languages ("100 ألف" vs "100K"). One spare character covers
 * the axis rounding its top tick up.
 */
export function yAxisWidth(longestLabel: string): number {
  return Math.max(40, (longestLabel.length + 1) * 7 + 8);
}

/**
 * Shared chart styling for recharts so every chart reads as one institutional
 * system: thin lines, horizontal hairline grid only, no axis lines, muted
 * series palette, typographic tooltips.
 */
export function getChartTokens(theme: Theme) {
  const mode = theme.palette.mode;
  const borders = contentBorders(theme);
  const palette = [...CONTENT_CHART_COLORS[mode]];

  const tick = {
    fill: theme.palette.text.secondary,
    fontSize: 12,
    fontFamily: CONTENT_FONT_FAMILY_VAR,
  };

  const tooltipContentStyle: CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${borders.default}`,
    borderRadius: 6,
    boxShadow: theme.shadows[8],
    fontSize: 13,
    fontFamily: CONTENT_FONT_FAMILY_VAR,
    padding: '8px 12px',
    color: theme.palette.text.primary,
    direction: theme.direction,
    textAlign: theme.direction === 'rtl' ? 'right' : 'left',
  };

  return {
    palette,
    /** Colour for series index i (wraps). */
    seriesColor: (i: number) => palette[i % palette.length],
    grid: {
      stroke: alpha(theme.palette.text.primary, mode === 'dark' ? 0.1 : 0.08),
      strokeDasharray: undefined as string | undefined,
      vertical: false,
    },
    axis: {
      tick,
      axisLine: false,
      tickLine: false,
      tickMargin: 8,
    },
    tooltip: {
      contentStyle: tooltipContentStyle,
      labelStyle: { color: theme.palette.text.secondary, fontSize: 12, marginBottom: 4 },
      itemStyle: { color: theme.palette.text.primary, padding: 0 },
      cursor: { fill: alpha(theme.palette.text.primary, 0.04) },
    },
    line: {
      strokeWidth: 1.5,
      dot: false,
      activeDot: { r: 3, strokeWidth: 0 },
    },
    area: {
      strokeWidth: 1.5,
      fillOpacity: 0.1,
    },
    bar: {
      maxBarSize: 28,
      radius: [2, 2, 0, 0] as [number, number, number, number],
    },
    pie: {
      innerRadius: '62%',
      outerRadius: '90%',
      paddingAngle: 0.5,
      cornerRadius: 1,
    },
    /** Spread onto recharts series: grow-in on mount, off under reduced motion. */
    animation: {
      isAnimationActive: !prefersReducedMotion(),
      animationDuration: 800,
      animationEasing: 'ease-out' as const,
    },
    legend: {
      wrapperStyle: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        paddingTop: 8,
      } as CSSProperties,
      iconSize: 8,
    },
  };
}

export type ChartTokens = ReturnType<typeof getChartTokens>;
